import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Avvisi in fondo allo schermo.
 *
 * Servono soprattutto per una cosa: dopo ogni restituzione l'operatore deve
 * poter tornare indietro senza cercare il chip. Il transponder gli è appena
 * scivolato nella cassa sbagliata, o ha scansionato quello del compagno di
 * squadra — e deve poterlo annullare **subito**, con un pollice, senza lasciare
 * il banco.
 *
 * Gli avvisi di errore invece non hanno azione e restano più a lungo: quelli
 * vanno letti.
 */

export type ToastTone = 'returned' | 'missing' | 'danger' | 'neutral';

export type ToastAction = {
  label: string;
  onClick: () => void | Promise<void>;
};

export type Toast = {
  id: number;
  tone: ToastTone;
  message: ReactNode;
  action?: ToastAction;
  durationMs: number;
};

type ToastInput = {
  tone?: ToastTone;
  message: ReactNode;
  action?: ToastAction;
  durationMs?: number;
};

type ToastContextValue = {
  push: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

/** Un avviso con azione resta più a lungo: c'è una decisione da prendere. */
const DEFAULT_DURATION_MS = 6000;
const ACTION_DURATION_MS = 9000;
const DANGER_DURATION_MS = 10000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextIdRef = useRef(1);
  const timersRef = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = nextIdRef.current;
      nextIdRef.current += 1;

      const tone = input.tone ?? 'neutral';
      const durationMs =
        input.durationMs ??
        (input.action
          ? ACTION_DURATION_MS
          : tone === 'danger'
            ? DANGER_DURATION_MS
            : DEFAULT_DURATION_MS);

      setToasts((current) => [...current, { id, tone, message: input.message, action: input.action, durationMs }]);

      const timer = window.setTimeout(() => dismiss(id), durationMs);
      timersRef.current.set(id, timer);

      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  return (
    /*
     * `polite`: un avviso che compare mentre l'operatore sta già digitando il
     * codice successivo non deve interrompere il lettore di schermo a metà
     * parola. Chi usa un lettore sente comunque l'esito, appena finisce.
     */
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.tone}`}>
          <span className="toast__text">{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                onDismiss(toast.id);
                void toast.action?.onClick();
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function useToasts(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToasts va usato dentro ToastProvider');
  return context;
}
